import React, { useRef, useEffect } from 'react';
import { Bold, Italic, Underline, Heading2, Heading3, List, ListOrdered, Quote, Link2, Eraser } from 'lucide-react';

const ToolbarButton = ({ onClick, title, testid, children }) => (
  <button
    type="button"
    title={title}
    data-testid={testid}
    onMouseDown={(e) => { e.preventDefault(); onClick(); }}
    className="w-9 h-9 flex items-center justify-center rounded-md text-gray-600 hover:bg-notaria-secondary hover:text-white transition-colors"
  >
    {children}
  </button>
);

/**
 * Lightweight WYSIWYG editor based on contentEditable + document.execCommand.
 * Emits HTML via onChange. Avoids heavy deps and React 19 compatibility issues.
 */
export const RichTextEditor = ({ value, onChange }) => {
  const ref = useRef(null);

  useEffect(() => {
    if (ref.current && value !== undefined && ref.current.innerHTML !== value) {
      ref.current.innerHTML = value || '';
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const emit = () => {
    if (ref.current) onChange(ref.current.innerHTML);
  };

  const exec = (command, arg = null) => {
    document.execCommand(command, false, arg);
    ref.current?.focus();
    emit();
  };

  const format = (tag) => exec('formatBlock', tag);

  const addLink = () => {
    const url = window.prompt('URL del enlace:');
    if (url) exec('createLink', url);
  };

  return (
    <div className="border-2 border-gray-200 rounded-lg overflow-hidden focus-within:border-notaria-secondary transition-colors">
      <div className="flex flex-wrap items-center gap-1 border-b border-gray-200 bg-gray-50 p-2" data-testid="editor-toolbar">
        <ToolbarButton onClick={() => exec('bold')} title="Negrita" testid="editor-bold"><Bold className="w-4 h-4" /></ToolbarButton>
        <ToolbarButton onClick={() => exec('italic')} title="Cursiva" testid="editor-italic"><Italic className="w-4 h-4" /></ToolbarButton>
        <ToolbarButton onClick={() => exec('underline')} title="Subrayado" testid="editor-underline"><Underline className="w-4 h-4" /></ToolbarButton>
        <div className="w-px h-6 bg-gray-300 mx-1" />
        <ToolbarButton onClick={() => format('h2')} title="Título" testid="editor-h2"><Heading2 className="w-4 h-4" /></ToolbarButton>
        <ToolbarButton onClick={() => format('h3')} title="Subtítulo" testid="editor-h3"><Heading3 className="w-4 h-4" /></ToolbarButton>
        <ToolbarButton onClick={() => format('p')} title="Párrafo" testid="editor-paragraph"><span className="text-xs font-semibold">P</span></ToolbarButton>
        <div className="w-px h-6 bg-gray-300 mx-1" />
        <ToolbarButton onClick={() => exec('insertUnorderedList')} title="Lista con viñetas" testid="editor-ul"><List className="w-4 h-4" /></ToolbarButton>
        <ToolbarButton onClick={() => exec('insertOrderedList')} title="Lista numerada" testid="editor-ol"><ListOrdered className="w-4 h-4" /></ToolbarButton>
        <ToolbarButton onClick={() => format('blockquote')} title="Cita" testid="editor-quote"><Quote className="w-4 h-4" /></ToolbarButton>
        <div className="w-px h-6 bg-gray-300 mx-1" />
        <ToolbarButton onClick={addLink} title="Insertar enlace" testid="editor-link"><Link2 className="w-4 h-4" /></ToolbarButton>
        <ToolbarButton onClick={() => exec('removeFormat')} title="Quitar formato" testid="editor-clear"><Eraser className="w-4 h-4" /></ToolbarButton>
      </div>
      <div
        ref={ref}
        contentEditable
        onInput={emit}
        onBlur={emit}
        data-testid="editor-content"
        className="article-content min-h-[320px] max-h-[520px] overflow-y-auto p-4 text-gray-700 leading-relaxed focus:outline-none prose max-w-none"
        suppressContentEditableWarning
      />
    </div>
  );
};

export default RichTextEditor;
